import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import logger from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
    baseURL: API,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// In-flight request deduplication for GET requests
const _pendingGets = new Map();

// Add auth token to requests + GET deduplication
api.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('clt_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Wrap api.get with deduplication
const _originalGet = api.get.bind(api);
api.get = function(url, config) {
    const key = url + (config?.params ? JSON.stringify(config.params) : '');
    const pending = _pendingGets.get(key);
    if (pending) return pending;
    
    const promise = _originalGet(url, config).finally(() => {
        _pendingGets.delete(key);
    });
    _pendingGets.set(key, promise);
    return promise;
};

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            sessionStorage.removeItem('clt_token');
            sessionStorage.removeItem('clt_user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [justLoggedIn, setJustLoggedIn] = useState(false);
    const [impersonating, setImpersonating] = useState(!!sessionStorage.getItem('clt_impersonation_original'));

    useEffect(() => {
        const token = sessionStorage.getItem('clt_token');
        const savedUser = sessionStorage.getItem('clt_user');
        
        if (token && savedUser) {
            setUser(JSON.parse(savedUser));
            // Verify token is still valid
            api.get('/auth/me')
                .then(res => setUser(res.data))
                .catch(() => {
                    sessionStorage.removeItem('clt_token');
                    sessionStorage.removeItem('clt_user');
                    setUser(null);
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (email, password) => {
        // Retry up to 2 times for transient network/server errors
        let lastError;
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const response = await api.post('/auth/login', { email, password });
                const { access_token, user: userData } = response.data;
                
                sessionStorage.setItem('clt_token', access_token);
                sessionStorage.setItem('clt_user', JSON.stringify(userData));
                setJustLoggedIn(true);
                setUser(userData);
                
                return userData;
            } catch (error) {
                lastError = error;
                const status = error.response?.status;
                // Only retry on server/network errors, not on auth failures
                if (status === 401 || status === 403) throw error;
                // Retry on 502, 504, timeout, network errors
                if (attempt < 2 && (!status || status >= 500)) {
                    await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
                    continue;
                }
                throw error;
            }
        }
        throw lastError;
    };

    const logout = () => {
        sessionStorage.removeItem('clt_token');
        sessionStorage.removeItem('clt_user');
        sessionStorage.removeItem('_x');
        sessionStorage.removeItem('clt_impersonation_original');
        setImpersonating(false);
        setUser(null);
        setJustLoggedIn(false);
    };

    // Superadmin: log in as another user for troubleshooting/visibility checks.
    // Stores the current (superadmin) token so we can hop back with one click.
    const impersonate = async (targetUserId) => {
        const currentToken = sessionStorage.getItem('clt_token');
        const currentUser = sessionStorage.getItem('clt_user');
        if (!currentToken || !currentUser) throw new Error('Not logged in');

        const response = await api.post(`/admin/users/${targetUserId}/impersonate`);
        const { access_token, user: targetUser } = response.data;

        // Only remember the ORIGINAL superadmin token — nested impersonations
        // keep pointing back to the very first superadmin session.
        if (!sessionStorage.getItem('clt_impersonation_original')) {
            sessionStorage.setItem('clt_impersonation_original', JSON.stringify({
                token: currentToken,
                user: JSON.parse(currentUser),
            }));
        }
        sessionStorage.setItem('clt_token', access_token);
        sessionStorage.setItem('clt_user', JSON.stringify(targetUser));
        setImpersonating(true);
        setUser(targetUser);
        return targetUser;
    };

    const stopImpersonating = () => {
        const original = sessionStorage.getItem('clt_impersonation_original');
        if (!original) return;
        const { token, user: originalUser } = JSON.parse(original);
        sessionStorage.setItem('clt_token', token);
        sessionStorage.setItem('clt_user', JSON.stringify(originalUser));
        sessionStorage.removeItem('clt_impersonation_original');
        setImpersonating(false);
        setUser(originalUser);
    };

    const clearJustLoggedIn = () => {
        setJustLoggedIn(false);
    };
    
    const refreshUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
            sessionStorage.setItem('clt_user', JSON.stringify(response.data));
            return response.data;
        } catch (error) {
            logger.error('Failed to refresh user:', error);
            return null;
        }
    };

    const value = {
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!user,
        justLoggedIn,
        clearJustLoggedIn,
        refreshUser,
        impersonate,
        stopImpersonating,
        isImpersonating: impersonating,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
};

const getAutoTheme = () => {
    // Abu Dhabi timezone GST+4 — dark during night (7pm-6am), light during day
    const now = new Date();
    const utcHours = now.getUTCHours();
    const abuDhabiHour = (utcHours + 4) % 24;
    // Day: 6am - 7pm = light, Night: 7pm - 6am = dark
    return (abuDhabiHour >= 6 && abuDhabiHour < 19) ? 'light' : 'dark';
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem('clt_theme');
        if (saved === 'auto' || !saved) return getAutoTheme();
        return saved;
    });
    const [themeMode, setThemeMode] = useState(() => {
        return localStorage.getItem('clt_theme_mode') || 'auto';
    });

    useEffect(() => {
        const root = window.document.documentElement;
        const applied = themeMode === 'auto' ? getAutoTheme() : themeMode;
        root.classList.remove('light', 'dark');
        root.classList.add(applied);
        setTheme(applied);
        localStorage.setItem('clt_theme', applied);
        localStorage.setItem('clt_theme_mode', themeMode);
    }, [themeMode]);

    // Re-check auto theme every minute
    useEffect(() => {
        if (themeMode !== 'auto') return;
        const interval = setInterval(() => {
            const auto = getAutoTheme();
            setTheme(prev => {
                if (prev !== auto) {
                    const root = window.document.documentElement;
                    root.classList.remove('light', 'dark');
                    root.classList.add(auto);
                    localStorage.setItem('clt_theme', auto);
                    return auto;
                }
                return prev;
            });
        }, 60000);
        return () => clearInterval(interval);
    }, [themeMode]);

    const toggleTheme = () => {
        // Cycle: auto → light → dark → auto
        setThemeMode(prev => {
            if (prev === 'auto') return 'light';
            if (prev === 'light') return 'dark';
            return 'auto';
        });
    };

    return (
        <ThemeContext.Provider value={{ theme, themeMode, setTheme, setThemeMode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

// API Functions
export const apiClient = api;

// Auth APIs
export const authApi = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    getMe: () => api.get('/auth/me'),
};

// User APIs
export const userApi = {
    getAll: (params) => api.get('/users', { params }),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
};

// Lead APIs
export const leadApi = {
    getAll: (params) => api.get('/leads', { params }),
    create: (data) => api.post('/leads', data),
    update: (id, data) => api.put(`/leads/${id}`, data),
    delete: (id) => api.delete(`/leads/${id}`),
    reactivate: (id, data) => api.post(`/leads/${id}/reactivate`, data),
};

// Student APIs
export const studentApi = {
    getAll: (params) => api.get('/students', { params }),
    update: (id, data) => api.put(`/students/${id}`, data),
};

// Payment APIs
export const paymentApi = {
    getAll: (params) => api.get('/payments', { params }),
    create: (data) => api.post('/payments', data),
    update: (id, data) => api.put(`/payments/${id}`, data),
};

// Notification APIs
export const notificationApi = {
    getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/read-all'),
};

// Dashboard APIs
export const dashboardApi = {
    getStats: (viewAs) => api.get('/dashboard/stats', { params: viewAs ? { view_as: viewAs } : {} }),
    getLeadFunnel: (viewAs) => api.get('/dashboard/lead-funnel', { params: viewAs ? { view_as: viewAs } : {} }),
    getPaymentSummary: (viewAs) => api.get('/dashboard/payment-summary', { params: viewAs ? { view_as: viewAs } : {} }),
    getViewableUsers: () => api.get('/dashboard/viewable-users'),
};

// Activity Log APIs
export const activityApi = {
    getAll: (params) => api.get('/activity-logs', { params }),
};

// Permission Context
const PermissionContext = createContext(null);

export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within PermissionProvider');
    }
    return context;
};

// Import shared module registry — single source of truth for all pages
import MODULE_HIERARCHY from '@/config/moduleRegistry';

// Default permissions based on role
const getDefaultPermissions = (role) => {
    const permissions = {};
    
    // Initialize all to none
    MODULE_HIERARCHY.forEach(module => {
        permissions[module.id] = { enabled: false, level: 'none', subPages: {} };
        module.subPages.forEach(sp => {
            permissions[module.id].subPages[sp.id] = 'none';
        });
    });
    
    // SSHR, Communication, IT Assets available to all
    permissions['sshr'] = { enabled: true, level: 'view', subPages: {
        sshr_dashboard: 'view', sshr_payslips: 'view', sshr_calendar: 'view',
        sshr_announcements: 'view', sshr_tasks: 'edit',
    }};
    permissions['communication'] = { enabled: true, level: 'view', subPages: {
        team_chat: 'view', knowledge_base: 'view',
    }};
    permissions['it_assets'] = { enabled: true, level: 'view', subPages: {
        it_assets_page: 'view',
    }};
    
    // Set role-specific defaults
    if (role === 'admin') {
        MODULE_HIERARCHY.forEach(module => {
            permissions[module.id] = { enabled: true, level: 'full', subPages: {} };
            module.subPages.forEach(sp => {
                permissions[module.id].subPages[sp.id] = 'full';
            });
        });
        permissions['settings'].subPages['access_control'] = 'view';
        permissions['settings'].subPages['password_resets'] = 'none';
    } else if (role === 'sales_manager') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'view' } };
        permissions['sales'] = { enabled: true, level: 'full', subPages: { 
            sales_crm: 'full', sales_dashboard: 'full', today_followups: 'full', leads_pool: 'full', approvals: 'full' 
        }};
        permissions['customer_service'] = { enabled: true, level: 'view', subPages: { 
            cs_kanban: 'view', cs_dashboard: 'view', customer_master: 'view' 
        }};
    } else if (role === 'team_leader') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['sales'] = { enabled: true, level: 'edit', subPages: { 
            sales_crm: 'edit', sales_dashboard: 'view', today_followups: 'edit', leads_pool: 'view', approvals: 'view' 
        }};
        // Team leaders do NOT access CS, Mentor, Marketing
        permissions['customer_service'] = { enabled: false, subPages: {} };
        permissions['mentor'] = { enabled: false, subPages: {} };
        permissions['marketing'] = { enabled: false, subPages: {} };
        permissions['bd'] = { enabled: false, subPages: {} };
    } else if (role === 'sales_executive') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['sales'] = { enabled: true, level: 'edit', subPages: { 
            sales_crm: 'edit', sales_dashboard: 'view', today_followups: 'edit', leads_pool: 'none', approvals: 'none' 
        }};
        // Sales executives do NOT access CS, Mentor, Marketing, or Call Log
        permissions['customer_service'] = { enabled: false, subPages: {} };
        permissions['mentor'] = { enabled: false, subPages: {} };
        permissions['marketing'] = { enabled: false, subPages: {} };
        permissions['bd'] = { enabled: false, subPages: {} };
    } else if (role === 'cs_head') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'view' } };
        permissions['customer_service'] = { enabled: true, level: 'full', subPages: { 
            cs_kanban: 'full', cs_dashboard: 'full', customer_master: 'full' 
        }};
    } else if (role === 'cs_agent') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['customer_service'] = { enabled: true, level: 'edit', subPages: { 
            cs_kanban: 'edit', cs_dashboard: 'view', customer_master: 'view' 
        }};
    } else if (role === 'mentor' || role === 'academic_master' || role === 'master_of_academics') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        const isMaster = role === 'academic_master' || role === 'master_of_academics';
        permissions['mentor'] = { enabled: true, level: isMaster ? 'full' : 'edit', subPages: { 
            mentor_crm: isMaster ? 'full' : 'edit', 
            mentor_dashboard: isMaster ? 'full' : 'view', 
            leaderboard: 'view' 
        }};
    } else if (role === 'hr') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['hr'] = { enabled: true, level: 'full', subPages: {
            hr_dashboard: 'full', employee_master: 'full', leave_management: 'full',
            attendance: 'full', biocloud_sync: 'full', payroll: 'edit',
            performance: 'full', hr_assets: 'full', hr_analytics: 'view',
            wfh_verification: 'full', approval_pipeline: 'full',
            department_performance: 'view', offboarding: 'full',
            payroll_pre_run: 'edit', timesheet_team: 'view',
            hr_approvals: 'full', leave_approvals: 'full', regularization_approvals: 'full',
            shift_master: 'full', shift_assignment: 'full', late_rules: 'full',
        }};
        permissions['settings'] = { enabled: true, level: 'view', subPages: {
            user_management: 'view', departments: 'edit', courses: 'none',
            access_control: 'none', role_management: 'none', teams_management: 'none',
            password_resets: 'none', audit_log: 'none', admin_settings: 'none'
        }};
    } else if (role === 'finance' || role === 'finance_manager' || role === 'finance_admin') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['finance'] = { enabled: true, level: 'full', subPages: { 
            finance_selector: 'full', commission_engine: 'edit', clt_dashboard: 'full', clt_payables: 'full', clt_receivables: 'full',
            finance_verifications: 'full', pending_settlements: 'full', journal_entries: 'full',
            treasury_dashboard: 'full', treasury_balances: 'full', budget_sheet: 'edit', pnl_dashboard: 'view', data_management: 'view',
            chart_of_accounts: 'full', bank_accounts: 'full', cost_centers: 'full', 
            payment_methods: 'full', payment_gateways: 'full', psp_mapping: 'full'
        }};
        permissions['customer_service'] = { enabled: true, level: 'view', subPages: { 
            cs_kanban: 'none', cs_dashboard: 'none', customer_master: 'view' 
        }};
    } else if (role === 'marketing' || role === 'marketing_admin') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['marketing'] = { enabled: true, level: 'full', subPages: { 
            marketing_dashboard: 'full', lead_connectors: 'full', marketing_settings: 'full',
            marketing_calendar: 'full', competitor_intelligence: 'full',
            market_analysis: 'full', content_studio: 'full',
        }};
        permissions['sales'] = { enabled: true, level: 'view', subPages: { 
            sales_crm: 'none', sales_dashboard: 'view', today_followups: 'none', leads_pool: 'view', approvals: 'none' 
        }};
    }
    
    return permissions;
};

// Build PATH_TO_SUBPAGE dynamically from the shared module registry
const PATH_TO_SUBPAGE = {};
MODULE_HIERARCHY.forEach(module => {
    module.subPages.forEach(sp => {
        if (sp.path) {
            PATH_TO_SUBPAGE[sp.path] = sp.id;
        }
    });
});

// SubPage ID to module mapping
const SUBPAGE_TO_MODULE = {};
MODULE_HIERARCHY.forEach(module => {
    module.subPages.forEach(sp => {
        SUBPAGE_TO_MODULE[sp.id] = module.id;
    });
});

export const PermissionProvider = ({ children }) => {
    const { user } = useAuth();
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (!user) {
                setPermissions(null);
                setLoading(false);
                return;
            }

            // Super admin and Admin have full access by definition.
            // Both bypass the DB-driven permission load — admin's saved
            // permissions can drift to 'none' on individual sub-pages (via
            // Access Control edits or stale seeds), which would falsely hide
            // entire modules even though the role is meant to be unrestricted.
            if (user.role === 'super_admin' || user.role === 'admin') {
                setPermissions({ isSuperAdmin: true, fullAccess: true, role: user.role });
                setLoading(false);
                return;
            }

            try {
                const res = await api.get('/user/permissions');
                if (res.data.permissions && Object.keys(res.data.permissions).length > 0) {
                    // Merge saved permissions with defaults for any missing modules
                    const defaults = getDefaultPermissions(user.role);
                    const savedPerms = res.data.permissions;
                    const merged = { ...defaults };
                    // Saved permissions override defaults for modules that exist in saved data
                    for (const key of Object.keys(savedPerms)) {
                        merged[key] = savedPerms[key];
                    }
                    setPermissions({
                        isSuperAdmin: false,
                        role: user.role,
                        ...merged
                    });
                } else {
                    // Use default permissions for role
                    setPermissions({
                        isSuperAdmin: false,
                        role: user.role,
                        ...getDefaultPermissions(user.role)
                    });
                }
            } catch (error) {
                // Use default permissions on error
                setPermissions({
                    isSuperAdmin: false,
                    role: user.role,
                    ...getDefaultPermissions(user.role)
                });
            }
            setLoading(false);
        };

        fetchPermissions();
    }, [user]);

    // Check if user can access a specific path
    const canAccess = (path) => {
        if (!permissions) return false;
        if (permissions.isSuperAdmin || permissions.fullAccess) return true;

        // Try direct path lookup
        let subPageId = PATH_TO_SUBPAGE[path];
        
        // Handle finance entity routes: /finance/clt/verifications → /finance/clt/verifications
        // Sidebar uses paths like /finance/verifications but actual route is /finance/:entity/verifications
        if (!subPageId && path.startsWith('/finance/')) {
            const parts = path.split('/').filter(Boolean); // ['finance', entity, subpath...]
            if (parts.length >= 3) {
                // Try matching /finance/clt/<subpath> pattern from the registry
                const subPath = parts.slice(2).join('/');
                const tryPaths = [
                    `/finance/clt/${subPath}`,
                    `/finance/treasury/${subPath}`,
                    `/finance/budgeting/${subPath}`,
                    `/finance/pnl/${subPath}`,
                    `/finance/data/${subPath}`,
                    `/finance/settings/${subPath}`,
                ];
                for (const tp of tryPaths) {
                    if (PATH_TO_SUBPAGE[tp]) {
                        subPageId = PATH_TO_SUBPAGE[tp];
                        break;
                    }
                }
            }
        }
        
        // Handle detail pages: /hr/employees/123 → check /hr/employees
        if (!subPageId && path.includes('/')) {
            const segments = path.split('/').filter(Boolean);
            // Try progressively shorter paths
            for (let len = segments.length - 1; len >= 1; len--) {
                const parentPath = '/' + segments.slice(0, len).join('/');
                if (PATH_TO_SUBPAGE[parentPath]) {
                    subPageId = PATH_TO_SUBPAGE[parentPath];
                    break;
                }
            }
        }
        
        // Paths not in registry — allow only common/shared paths
        if (!subPageId) {
            const commonPaths = ['/home', '/welcome', '/settings', '/profile', '/notifications', '/chat', '/claret', '/knowledge-base', '/sshr', '/sshr/calendar', '/sshr/payslips', '/sshr/announcements', '/hr/tasks'];
            return commonPaths.some(cp => path === cp || path.startsWith(cp + '/'));
        }

        const moduleId = SUBPAGE_TO_MODULE[subPageId];
        if (!moduleId) return true;

        const modulePerms = permissions[moduleId];
        if (!modulePerms || !modulePerms.enabled) return false;

        const subPagePerm = modulePerms.subPages?.[subPageId];
        return subPagePerm && subPagePerm !== 'none';
    };

    // Check permission level for a path (none, view, edit, full)
    const getPermissionLevel = (path) => {
        if (!permissions) return 'none';
        if (permissions.isSuperAdmin || permissions.fullAccess) return 'full';

        const subPageId = PATH_TO_SUBPAGE[path];
        if (!subPageId) return 'full';

        const moduleId = SUBPAGE_TO_MODULE[subPageId];
        if (!moduleId) return 'full';

        const modulePerms = permissions[moduleId];
        if (!modulePerms || !modulePerms.enabled) return 'none';

        return modulePerms.subPages?.[subPageId] || 'none';
    };

    // Check if user can edit on a path
    const canEdit = (path) => {
        const level = getPermissionLevel(path);
        return level === 'edit' || level === 'full';
    };

    // Check if user has full access on a path
    const hasFullAccess = (path) => {
        return getPermissionLevel(path) === 'full';
    };

    // Check module-level access
    const canAccessModule = (moduleId) => {
        if (!permissions) return false;
        if (permissions.isSuperAdmin || permissions.fullAccess) return true;

        const modulePerms = permissions[moduleId];
        return modulePerms && modulePerms.enabled;
    };

    const value = {
        permissions,
        loading,
        canAccess,
        canEdit,
        hasFullAccess,
        getPermissionLevel,
        canAccessModule,
        PATH_TO_SUBPAGE,
        SUBPAGE_TO_MODULE,
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

// Permission APIs
export const permissionApi = {
    getRolePermissions: (roleId) => api.get(`/roles/${roleId}/permissions`),
    saveRolePermissions: (roleId, permissions) => api.put(`/roles/${roleId}/permissions`, permissions),
    getCurrentUserPermissions: () => api.get('/user/permissions'),
};

export default api;
